import React, { useState } from 'react';
import { IconInfo } from './Icons';
import { PnlScalingInfoModal } from './PnlScalingInfoModal';

export function formatCapitalBaseLabel(capitalBase = 100000) {
  const base = Number(capitalBase);
  const value = Number.isFinite(base) && base > 0 ? base : 100000;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function buildMonthlyReturnsInfoParagraphs(capitalBase = 100000, method = 'fixed') {
  const baseLabel = formatCapitalBaseLabel(capitalBase);
  const usesAccountPct = String(method || '').toLowerCase() === 'compound';
  if (usesAccountPct) {
    return [
      'The main Monthly return number is the trailing return over roughly the last 30 calendar days ending on the last date in your selected range. Math: ((value at end ÷ value about 30 days earlier) − 1) × 100.',
      'This strategy\'s category is set to Account % (Settings → Method → Monthly returns). Calendar months use month-over-month growth: (this month\'s last value ÷ prior month\'s last value − 1) × 100.',
      'That matches how the account actually moved, and it matches SPY. The months compound to the period total — they will not add to it.',
      'Switch the category to Fixed $100k if you want each month to be (dollar P&L ÷ capital base) so the months add to total return.',
    ];
  }
  return [
    'The main Monthly return number is the trailing return over roughly the last 30 calendar days ending on the last date in your selected range. Math: ((value at end ÷ value about 30 days earlier) − 1) × 100.',
    `This strategy's category is set to Fixed $100k (Settings → Method → Monthly returns). Each calendar month's % uses a fixed capital base (${baseLabel}, from your Capital Base setting).`,
    'Calendar-month % = (that month\'s dollar P&L ÷ capital base) × 100. Dollar P&L is the change in the strategy curve from the prior month\'s last value through this month\'s last value.',
    'Because each month divides by the same notional base, the calendar months in your selected window add up to the period total return (before small rounding). This is fixed-size account reporting, not compounded month-over-month % on a growing balance.',
    'Benchmark columns (e.g. SPY) still use month-over-month %: (month-end ÷ month-start − 1) × 100, which compounds across months.',
  ];
}

export const MONTHLY_RETURNS_INFO_TITLE = 'Monthly return — how it works';

/**
 * Info (i) control that opens the monthly-returns math modal.
 */
export function MonthlyReturnsInfoButton({
  capitalBase = 100000,
  method = 'fixed',
  className,
  iconSize = 14,
  ariaLabel = 'Open monthly return explanation',
  iconColor,
}) {
  const [open, setOpen] = useState(false);
  const btnClass = className || 'monthly-returns-info-btn';
  const paragraphs = buildMonthlyReturnsInfoParagraphs(capitalBase, method);

  return (
    <>
      <button
        type="button"
        className={btnClass}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <IconInfo size={iconSize} color={iconColor || 'currentColor'} />
      </button>
      <PnlScalingInfoModal
        open={open}
        onClose={() => setOpen(false)}
        title={MONTHLY_RETURNS_INFO_TITLE}
        paragraphs={paragraphs}
      />
    </>
  );
}
